import React from 'react';
import { useAgency } from '../context/AgencyContext';
import api from '../services/api';
import { openElevationDialog } from './PlatformElevation';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Badge } from './ui/badge';
import { Buildings, CaretDown } from '@phosphor-icons/react';

const AgencySwitcher = () => {
  const { agencies, currentAgency, switchAgency, isAzaiStaff, loading } = useAgency();

  // Only show switcher for AZAI staff with multiple agencies
  if (!isAzaiStaff || agencies.length <= 1) {
    return null;
  }

  const handleSwitch = async (agency) => {
    // Phase 4: platform staff need an active elevation for the target agency.
    try {
      const r = await api.get('/platform-access/status', { params: { agency_id: agency.id } });
      if (r.data.active) {
        switchAgency(agency);
        window.location.reload();
        return;
      }
    } catch {
      // fall through to the elevation dialog
    }
    openElevationDialog(agency.id, agency.name);
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-slate-100 rounded-lg">
        <div className="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-700';
      case 'demo':
        return 'bg-purple-100 text-purple-700';
      case 'inactive':
        return 'bg-slate-100 text-slate-600';
      case 'suspended':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-slate-100 text-slate-600';
    }
  };

  return (
    <div className="flex items-center gap-2" data-testid="agency-switcher">
      <Buildings size={20} className="text-slate-500" />
      <Select
        value={currentAgency?.id || ''}
        onValueChange={(value) => {
          const agency = agencies.find((a) => a.id === value);
          if (agency) {
            handleSwitch(agency);
          }
        }}
      >
        <SelectTrigger className="w-[220px] bg-white border-slate-200" data-testid="agency-select-trigger">
          <SelectValue placeholder="Select Agency">
            {currentAgency && (
              <div className="flex items-center gap-2">
                <span className="truncate">{currentAgency.name}</span>
                {currentAgency.status === 'demo' && (
                  <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                    Demo
                  </Badge>
                )}
              </div>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <div className="px-2 py-1.5 text-xs font-medium text-slate-500 uppercase tracking-wider">
            Switch Agency
          </div>
          {agencies.map((agency) => (
            <SelectItem key={agency.id} value={agency.id} data-testid={`agency-option-${agency.id}`}>
              <div className="flex items-center justify-between w-full gap-2">
                <span className="truncate">{agency.name}</span>
                <Badge variant="outline" className={`text-xs ${getStatusColor(agency.status)}`}>
                  {agency.status}
                </Badge>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default AgencySwitcher;
